/**
 * Em'z Blendz Variant Handler
 * 
 * Bridges the gap between variant picker radio/select inputs and the
 * product form's hidden variant ID input. Listens for option changes,
 * matches the selected combination to a variant, updates the form,
 * price display, availability, and dispatches variant:change events.
 *
 * Works with both button (radio) and dropdown (select) variant pickers
 * from the Impulse/Archetype theme.
 */
(function() {
  'use strict';

  // Find all product sections on the page
  var productSections = document.querySelectorAll('[data-section-type="product"]');

  productSections.forEach(function(section) {
    initVariantHandler(section);
  });

  function initVariantHandler(section) {
    // Parse the variants JSON
    var variantJsonEl = section.querySelector('[data-variant-json]');
    if (!variantJsonEl) return;

    var variants;
    try {
      variants = JSON.parse(variantJsonEl.textContent.trim());
    } catch (e) {
      console.error('EMZ Variant Handler: Could not parse variant JSON', e);
      return;
    }

    if (!variants || variants.length === 0) return;

    // Find the hidden ID input (the one inside the main product form, not sticky bar)
    var form = section.querySelector('form[id^="AddToCartForm"]');
    if (!form) {
      // Fallback: find any product-form that's not in the sticky bar
      form = section.querySelector('.product-form:not(.emz-sticky-form) form');
    }
    if (!form) {
      form = section.querySelector('form.product-form');
    }

    var hiddenInput = form ? form.querySelector('input[name="id"]') : null;
    if (!hiddenInput) {
      // Last resort: find any input[name="id"] that's not inside the sticky bar
      var allInputs = section.querySelectorAll('input[name="id"]');
      var stickyBar = document.getElementById('EmzStickyBar');
      for (var i = 0; i < allInputs.length; i++) {
        if (!stickyBar || !stickyBar.contains(allInputs[i])) {
          hiddenInput = allInputs[i];
          break;
        }
      }
    }

    if (!hiddenInput) {
      console.error('EMZ Variant Handler: Could not find hidden variant ID input');
      return;
    }

    // Get all variant inputs (radios and selects)
    var variantInputs = section.querySelectorAll('[data-variant-input]');
    if (variantInputs.length === 0) return;

    // Listen for changes on all variant inputs
    variantInputs.forEach(function(input) {
      input.addEventListener('change', function() {
        onVariantChange(section, variants, hiddenInput);
      });
    });

    // Also handle URL-based variant selection on page load
    var urlParams = new URLSearchParams(window.location.search);
    var urlVariantId = urlParams.get('variant');
    if (urlVariantId) {
      var urlVariant = variants.find(function(v) {
        return v.id == urlVariantId;
      });
      if (urlVariant) {
        updateEverything(section, urlVariant, hiddenInput);
      }
    }
  }

  function onVariantChange(section, variants, hiddenInput) {
    // Read current selections from all option fieldsets/selects
    var selectedOptions = getSelectedOptions(section);

    // Find the variant that matches all selected options
    var matchedVariant = null;
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var isMatch = true;

      for (var j = 0; j < selectedOptions.length; j++) {
        if (variant.options[j] !== selectedOptions[j]) {
          isMatch = false;
          break;
        }
      }

      if (isMatch) {
        matchedVariant = variant;
        break;
      }
    }

    if (matchedVariant) {
      updateEverything(section, matchedVariant, hiddenInput);
    }
  }

  function getSelectedOptions(section) {
    var options = [];

    // Check for radio buttons first (button picker)
    var fieldsets = section.querySelectorAll('.variant-input-wrap');
    if (fieldsets.length > 0) {
      fieldsets.forEach(function(fieldset) {
        var checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) {
          options.push(checked.value);
        }
      });
    }

    // Check for select dropdowns
    var selects = section.querySelectorAll('[data-variant-input][data-index]');
    selects.forEach(function(select) {
      if (select.tagName === 'SELECT') {
        options.push(select.value);
      }
    });

    // Deduplicate: if we got options from both radios and selects in same fieldset,
    // prefer the organized approach by index
    if (options.length === 0) {
      // Fallback: just get all checked radios or selected options
      var allRadios = section.querySelectorAll('[data-variant-input]:checked');
      allRadios.forEach(function(radio) {
        options.push(radio.value);
      });
    }

    return options;
  }

  function updateEverything(section, variant, hiddenInput) {
    // 1. Update the hidden form input
    hiddenInput.value = variant.id;

    // 2. Update the URL
    if (window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    // 3. Update price display
    updatePrice(section, variant);

    // 4. Update add-to-cart button state
    updateAddToCart(section, variant);

    // 5. Update SKU
    updateSku(section, variant);

    // 6. Dispatch variant:change event for sticky bar, subscription widget, etc.
    document.dispatchEvent(new CustomEvent('variant:change', {
      detail: {
        variant: variant,
        sectionId: section.dataset.sectionId
      }
    }));

    // 7. Update color swatch label if applicable
    updateColorLabel(section, variant);

    // 8. Update featured image if variant has one
    updateVariantImage(section, variant);
  }

  function updatePrice(section, variant) {
    var moneyFormat = theme && theme.settings ? theme.settings.moneyFormat : '${{amount}}';

    // Current price
    var priceEl = section.querySelector('[data-product-price]');
    if (priceEl) {
      priceEl.innerHTML = formatMoney(variant.price, moneyFormat);
      if (variant.compare_at_price > variant.price) {
        priceEl.classList.add('on-sale');
      } else {
        priceEl.classList.remove('on-sale');
      }
    }

    // Compare-at price
    var compareEl = section.querySelector('[data-compare-price]');
    var comparePriceWrap = section.querySelector('[data-product-price-wrap]');
    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        compareEl.innerHTML = formatMoney(variant.compare_at_price, moneyFormat);
        if (comparePriceWrap) comparePriceWrap.classList.remove('hide');
      } else {
        compareEl.innerHTML = '';
        if (comparePriceWrap) comparePriceWrap.classList.add('hide');
      }
    }

    // Save price
    var savePriceEl = section.querySelector('[data-save-price]');
    if (savePriceEl) {
      if (variant.compare_at_price > variant.price) {
        var savedAmount;
        var saveType = (theme && theme.settings) ? theme.settings.saveType : 'dollar';
        if (saveType === 'dollar') {
          savedAmount = formatMoney(variant.compare_at_price - variant.price, moneyFormat);
        } else {
          savedAmount = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) + '%';
        }
        var saveString = (theme && theme.strings && theme.strings.savePrice)
          ? theme.strings.savePrice.replace('[saved_amount]', savedAmount)
          : 'Save ' + savedAmount;
        savePriceEl.innerHTML = saveString;
        savePriceEl.classList.remove('hide');
      } else {
        savePriceEl.classList.add('hide');
      }
    }

    // Unit price
    var unitPriceWrapper = section.querySelector('[data-unit-price-wrapper]');
    if (unitPriceWrapper) {
      if (variant.unit_price_measurement) {
        unitPriceWrapper.classList.remove('hide');
        var unitPriceEl = section.querySelector('[data-unit-price]');
        if (unitPriceEl) {
          unitPriceEl.innerHTML = formatMoney(variant.unit_price, moneyFormat);
        }
        var unitBaseEl = section.querySelector('[data-unit-base]');
        if (unitBaseEl) {
          var ref = variant.unit_price_measurement.reference_value !== 1
            ? variant.unit_price_measurement.reference_value
            : '';
          unitBaseEl.textContent = ref + variant.unit_price_measurement.reference_unit;
        }
      } else {
        unitPriceWrapper.classList.add('hide');
      }
    }
  }

  function updateAddToCart(section, variant) {
    var btn = section.querySelector('[data-add-to-cart]');
    var btnText = section.querySelector('[data-add-to-cart-text]');

    // Don't update buttons inside the sticky bar
    var stickyBar = document.getElementById('EmzStickyBar');
    if (btn && stickyBar && stickyBar.contains(btn)) {
      btn = null;
      // Try to find the non-sticky-bar button
      var allBtns = section.querySelectorAll('[data-add-to-cart]');
      for (var i = 0; i < allBtns.length; i++) {
        if (!stickyBar.contains(allBtns[i])) {
          btn = allBtns[i];
          btnText = allBtns[i].querySelector('[data-add-to-cart-text]');
          break;
        }
      }
    }

    if (!btn) return;

    var defaultText = btn.dataset.originalText ||
      (btnText ? btnText.dataset.defaultText : null) ||
      'Add to cart';

    if (variant.available) {
      btn.disabled = false;
      if (btnText) {
        btnText.textContent = defaultText;
      }
    } else {
      btn.disabled = true;
      if (btnText) {
        btnText.textContent = (theme && theme.strings && theme.strings.soldOut)
          ? theme.strings.soldOut
          : 'Sold Out';
      }
    }
  }

  function updateSku(section, variant) {
    var skuEl = section.querySelector('[data-sku]');
    if (skuEl) {
      skuEl.textContent = variant.sku || '';
    }
  }

  function updateColorLabel(section, variant) {
    // Update color swatch label text when a color option changes
    var colorLabels = section.querySelectorAll('[data-variant-color-label]');
    colorLabels.forEach(function(label) {
      var optionIndex = parseInt(label.dataset.optionIndex, 10);
      if (variant.options && variant.options[optionIndex] !== undefined) {
        label.textContent = variant.options[optionIndex];
      }
    });
  }

  function updateVariantImage(section, variant) {
    // If the variant has an associated image, try to switch to it
    if (!variant.featured_image) return;

    var imageId = variant.featured_image.id;
    // Look for a thumbnail or slide with this image ID and click/activate it
    var thumb = section.querySelector(
      '[data-image-id="' + imageId + '"], ' +
      '[data-media-id="' + imageId + '"], ' +
      'a[href*="' + variant.featured_image.src.split('?')[0].split('/').pop() + '"]'
    );
    if (thumb) {
      thumb.click();
    }
  }

  /**
   * Format money using Shopify's money format
   * Simplified version that handles common formats
   */
  function formatMoney(cents, format) {
    // Use theme's formatter if available
    if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, format);
    }

    // Fallback formatter
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function addCommas(moneyString) {
      return moneyString.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = addCommas((cents / 100.0).toFixed(2));
        break;
      case 'amount_no_decimals':
        value = addCommas(Math.round(cents / 100.0));
        break;
      case 'amount_with_comma_separator':
        value = (cents / 100.0).toFixed(2).replace(/\./, ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = addCommas(Math.round(cents / 100.0)).replace(/\./, ',');
        break;
      default:
        value = addCommas((cents / 100.0).toFixed(2));
    }

    return formatString.replace(placeholderRegex, value);
  }

})();
